import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {Alert, Button, Card, TextField} from '@betareader/design-system';
import {discoverSamples} from './api';

const Page = styled.main`
  margin: 0 auto;
  max-width: 1100px;
  padding: 20px;
`;

const Filters = styled.form`
  align-items: end;
  display: flex;
  flex-wrap: wrap;
  gap: var(--br-space-3);
  margin-bottom: var(--br-space-4);
`;

const Cards = styled.div`
  display: grid;
  gap: var(--br-space-4);
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
`;

const SampleLink = styled(Link)`
  color: var(--br-text);
  text-decoration: none;

  &:focus-visible .br-card, &:hover .br-card {
    border-color: var(--br-accent);
    outline: 2px solid var(--br-focus);
    outline-offset: 2px;
  }
`;

const Cover = styled.img`
  aspect-ratio: 3 / 2;
  border-radius: var(--br-control-radius);
  object-fit: cover;
  width: 100%;
`;

const Tags = styled.div`
  color: var(--br-muted);
  display: flex;
  flex-wrap: wrap;
  gap: var(--br-space-2);
`;

const Tag = styled.span`
  background: var(--br-raised-surface);
  border: 1px solid var(--br-border);
  border-radius: 999px;
  padding: var(--br-space-1) var(--br-space-2);
`;

const SampleChapterDiscover = () => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [hasAudioOnly, setHasAudioOnly] = useState(false);

  const load = async ({query = q, audioOnly = hasAudioOnly} = {}) => {
    setItems(null);
    setError(null);
    try {
      const data = await discoverSamples({
        q: query || undefined,
        hasAudio: audioOnly ? 'true' : undefined,
      });
      setItems(data);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    load({query: '', audioOnly: false});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAudioOnly = () => {
    const next = !hasAudioOnly;
    setHasAudioOnly(next);
    load({audioOnly: next});
  };

  return (
    <Page>
      <h1>Sample chapters</h1>
      <p>
        Read first chapters from indie authors and tell them whether you'd keep
        reading. Some samples have audio narration, others are text only.
      </p>
      <Filters onSubmit={event => { event.preventDefault(); load(); }}>
        <TextField
          label="Search sample chapters"
          placeholder="Search title, author or description"
          value={q}
          onChange={event => setQ(event.target.value)}
        />
        <Button type="button" variant={hasAudioOnly ? 'primary' : 'secondary'}
          aria-pressed={hasAudioOnly} onClick={toggleAudioOnly}>
          Audio only
        </Button>
        <Button type="submit">Search</Button>
      </Filters>

      {error && <Alert variant="error">{error}</Alert>}
      {!items && !error && <p role="status">Loading sample chapters…</p>}
      {items && items.length === 0 && <p>No sample chapters yet.</p>}

      <Cards>
        {items?.map(sample => (
          <SampleLink key={sample._id} to={`/sample-chapters/read/${sample._id}`}>
            <Card>
              {sample.coverUrl && <Cover alt="" src={sample.coverUrl} />}
              <h2>{sample.title}</h2>
              <p>by {sample.authorName || 'Unknown'}</p>
              <p>{sample.description}</p>
              <Tags>
                <Tag>Variant {sample.variantLabel}</Tag>
                <Tag>Text</Tag>
                <Tag>{sample.hasAudio ? 'Audio' : 'No audio'}</Tag>
                {sample.wordCount ? <Tag>{sample.wordCount} words</Tag> : null}
              </Tags>
            </Card>
          </SampleLink>
        ))}
      </Cards>
    </Page>
  );
};

export default SampleChapterDiscover;
